import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/shared/lib/api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";

interface MediaFile {
  name: string;
  kind: string;
  url: string;
  bytes: number;
}

interface MissionMedia {
  missionId: number;
  media: Record<string, MediaFile[]>;
  count: number;
}

export const missionMediaKeys = {
  list: (missionId: string | number) =>
    ["missionkit", "media", String(missionId)] as const,
  briefing: (missionId: string | number) =>
    ["missionkit", "briefing", String(missionId)] as const,
};

/**
 * What is staged on disk for one mission, grouped by kind.
 *
 * One query behind every consumer, so the text and the video cost a single
 * request between them rather than one each.
 */
function useMissionMedia(missionId?: string | number) {
  return useQuery({
    queryKey: missionMediaKeys.list(missionId ?? ""),
    enabled: missionId != null && missionId !== "",
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await apiClient.get(
        API_ENDPOINTS.mission.kitMedia(missionId!),
      );
      return (data?.result ?? data) as MissionMedia;
    },
  });
}

/**
 * The text staged for a mission, ready to print on the paper.
 *
 * Listing first rather than guessing a filename means an author can call it
 * anything and rename it later without touching the app.
 *
 * Returns "" when the mission has no txt staged. That is the normal state for
 * a mission nobody has written for yet, so it renders as a blank sheet rather
 * than an error — the paper is the point, the text is the bonus.
 */
export function useMissionBriefing(missionId?: string | number) {
  const { data: media } = useMissionMedia(missionId);
  const url = media?.media?.txt?.[0]?.url;

  return useQuery({
    queryKey: missionMediaKeys.briefing(missionId ?? ""),
    enabled: Boolean(url),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      // Plain text, not the JSON envelope every other call returns — this one
      // reads a file off disk, so ask axios not to parse it.
      const { data: body } = await apiClient.get<string>(url!, {
        responseType: "text",
        transformResponse: [(raw: string) => raw],
      });
      return typeof body === "string" ? body : "";
    },
  });
}

/**
 * The mission's own clip, played once the glasses are on.
 *
 * The first video staged for the mission — one clip per mission is the
 * convention, so anything else in there is a staging mistake rather than a
 * choice for the app to make. Undefined when nothing is staged, and the
 * glasses then simply close as they did before.
 */
export function useMissionVideoUrl(missionId?: string | number) {
  const { data: media } = useMissionMedia(missionId);
  return media?.media?.video?.[0]?.url;
}
